import { type FormEvent, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { createVaccination } from '../../api/clinical'
import { getPatient } from '../../api/patients'
import { getMyPractices } from '../../api/staff'
import { Button } from '../../components/ui/Button'
import { Card, ErrorBox, SectionTitle } from '../../components/ui/Card'
import { FormField, inputClass, selectClass } from '../../components/ui/FormField'
import { useToast } from '../../components/ui/Toast'
import { useAuth } from '../../context/AuthContext'
import type { MyPractice } from '../../types/clinical'

export default function VaccinationForm() {
  const { id: patientIdStr } = useParams<{ id: string }>()
  const patientId = Number(patientIdStr)
  const navigate = useNavigate()
  const toast = useToast()
  const { user } = useAuth()

  const [patientName, setPatientName] = useState('')
  const [practices, setPractices] = useState<MyPractice[]>([])
  const [practiceId, setPracticeId] = useState('')
  const [vaccineName, setVaccineName] = useState('')
  const [dateGiven, setDateGiven] = useState(new Date().toISOString().split('T')[0])
  const [nextDue, setNextDue] = useState('')
  const [batchNumber, setBatchNumber] = useState('')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<'practiceId' | 'vaccineName' | 'dateGiven', string>>>({})

  useEffect(() => {
    Promise.all([getPatient(patientId), getMyPractices()])
      .then(([patient, myPractices]) => {
        setPatientName(patient.name)
        setPractices(myPractices)
        if (myPractices.length === 1) setPracticeId(String(myPractices[0].id))
      })
      .catch(() => setError('Failed to load form data.'))
      .finally(() => setLoading(false))
  }, [patientId])

  const validate = (): boolean => {
    const errs: typeof fieldErrors = {}
    if (!practiceId) errs.practiceId = 'Please select a practice.'
    if (!vaccineName.trim()) errs.vaccineName = 'Vaccine name is required.'
    if (!dateGiven) errs.dateGiven = 'Date is required.'
    setFieldErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (submitting || !validate()) return
    setSubmitting(true)
    setError(null)
    try {
      await createVaccination(patientId, {
        practice_id: Number(practiceId),
        vaccine_name: vaccineName.trim(),
        date_given: dateGiven,
        next_due_date: nextDue || null,
        batch_number: batchNumber.trim() || null,
      })
      toast.success('Vaccination recorded.')
      void navigate(-1)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.')
      setSubmitting(false)
    }
  }

  if (loading) return <div className="text-text-2 p-8">Loading…</div>

  if (user?.role === 'org_admin') {
    return (
      <div className="p-8">
        <ErrorBox>Organization admins don't have access to clinical documentation.</ErrorBox>
      </div>
    )
  }

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => void navigate(-1)}
          className="bg-transparent border-0 cursor-pointer text-accent text-sm font-medium p-0"
        >
          ← Back
        </button>
        <h1 className="text-[1.4rem] font-bold text-text-heading m-0">
          Record Vaccination {patientName ? `— ${patientName}` : ''}
        </h1>
      </div>

      <Card>
        <SectionTitle>Vaccination Details</SectionTitle>
        {error && <ErrorBox className="mb-4">{error}</ErrorBox>}

        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
          <FormField label="Practice" required error={fieldErrors.practiceId}>
            <select
              value={practiceId}
              onChange={(e) => { setPracticeId(e.target.value); setFieldErrors(p => ({ ...p, practiceId: undefined })) }}
              className={selectClass(!!fieldErrors.practiceId)}
            >
              <option value="">Select practice…</option>
              {practices.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
            </select>
          </FormField>

          <FormField label="Vaccine name" required error={fieldErrors.vaccineName}>
            <input
              type="text"
              value={vaccineName}
              onChange={(e) => { setVaccineName(e.target.value); setFieldErrors(p => ({ ...p, vaccineName: undefined })) }}
              placeholder="e.g. C5, Rabies, Parvovirus…"
              className={inputClass(!!fieldErrors.vaccineName)}
            />
          </FormField>

          <FormField label="Date given" required error={fieldErrors.dateGiven}>
            <input
              type="date"
              value={dateGiven}
              onChange={(e) => { setDateGiven(e.target.value); setFieldErrors(p => ({ ...p, dateGiven: undefined })) }}
              className={inputClass(!!fieldErrors.dateGiven)}
            />
          </FormField>

          <FormField label="Next due date" hint="Optional — leave blank if not applicable">
            <input type="date" value={nextDue} onChange={(e) => setNextDue(e.target.value)} className={inputClass()} />
          </FormField>

          <FormField label="Batch / lot number" hint="Optional">
            <input type="text" value={batchNumber} onChange={(e) => setBatchNumber(e.target.value)} placeholder="e.g. A12345" className={inputClass()} />
          </FormField>

          <div className="flex gap-3 mt-7 pt-6 border-t border-border">
            <Button type="submit" disabled={submitting}>{submitting ? 'Saving…' : 'Record Vaccination'}</Button>
            <Button type="button" variant="outline" onClick={() => void navigate(-1)}>Cancel</Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
